// Libraries
import { AfterViewInit, Component, effect, EventEmitter, inject, input, Input, OnChanges, OnDestroy, Output, Renderer2, Signal, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule, ThemePalette } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { NgxMatDatepickerInput, NgxMatDatetimepicker } from '@ngxmc/datetime-picker';
import { endOfDay, formatISO, subYears } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Subject, takeUntil } from 'rxjs';

// Services
import { ProjectService } from 'src/app/services/api/projects/project.service';

// Interfaces
import { Project } from 'src/app/interfaces/api/project';

// Classes
import { UiTool } from 'src/app/classes/shared/ui.tools';

// UI
import { ButtonComponent } from 'ui-library';

const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'dd/MM/yyyy',
  },
  display: {
    dateInput: 'dd/MM/yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'dd/MM/yyyy',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

@Component({
  selector: 'app-range-picker',
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: fr },
    { provide: DateAdapter, useClass: DateFnsAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
  ],
  imports: [
    MatIconModule,
    MatInputModule,
    TranslateModule,
    MatFormFieldModule,
    MatNativeDateModule,
    FormsModule,
    MatDatepickerModule,
    MatButtonModule,
    ReactiveFormsModule,
    NgxMatDatepickerInput,
    NgxMatDatetimepicker,
    ButtonComponent
  ],
  standalone: true,
  templateUrl: './range-picker.html',
  styleUrls: ['./range-picker.scss']
})

export class RangePickerComponent implements OnChanges, AfterViewInit, OnDestroy {
  // Services
  private readonly projectService: ProjectService = inject(ProjectService);
  private _adapter: DateAdapter<any> = inject(DateAdapter);
  private renderer: Renderer2 = inject(Renderer2);
  private readonly uiTool: UiTool = inject(UiTool);

  // Components API
  @Input() startDate: string = '';
  @Input() endDate: string = '';
  @Input() startLabel: string = 'dashboard.start_date';
  @Input() endLabel: string = 'dashboard.end_date';
  @Input() showLabels: boolean = true;
  @Input() showPlaceholder: boolean = false;
  @Input() max: string = '';
  @Input() min: string = '';
  @Input() showSpinners: boolean = true;
  @Input() showSeconds: boolean = false;
  @Input() showMeridian: boolean = false;
  @Input() stepHour: number = 1;
  @Input() stepMinute: number = 1;
  @Input() stepSecond: number = 1;
  @Input() touchUi: boolean = false;
  @Input() color: ThemePalette = 'accent';
  @Input() disabled: boolean = false;
  @Input() disableMinute: boolean = false;
  @Output() change = new EventEmitter<{ startDate: string; endDate: string }>();

  @ViewChild('startDatePicker', { static: false }) startDatePicker: any;
  @ViewChild('endDatePicker', { static: false }) endDatePicker: any;

  // Variables
  public minDate: Date = subYears(new Date(), 10);
  public maxDate: Date = endOfDay(new Date());
  datesColors: any[] = [];
  private unlistenFns: (() => void)[] = [];
  stateTimeout: any;
  projectCreationDate?: any;
  destroy$ = new Subject<void>();

  // Signals
  readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;
  readonly activity = input<any>(null, { alias: 'activity' });

  constructor() {
    this._adapter.setLocale(fr);

    // Effect: Change project
    effect(() => {
      const project = this.currentProject();

      if (!project?.id) return;

      this.projectCreationDate = project.createdDate;
      this.datesColors = [];
    });

    // Effect: Change activity
    effect(() => {
      const activity = this.activity();

      if (!activity || !activity.details || activity.details.length === 0) {
        this.datesColors = [];
        return;
      }

      this.datesColors = this.uiTool.processActivityToDatesColors(activity);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['min'] || changes['max']) {
      this.initializeDates();
    }
  }

  ngAfterViewInit() {
    this._setupPickerListeners(this.startDatePicker);
    this._setupPickerListeners(this.endDatePicker);
  }

  ngOnDestroy() {
    clearTimeout(this.stateTimeout);

    if (this.unlistenFns.length) {
      this.unlistenFns.forEach(unlisten => unlisten());
      this.unlistenFns = [];
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  onStartDateChange(event: any): void {
    this.startDate = formatISO(new Date(event.value), { representation: 'complete' });
    this.onPickerDateChanged();
  }

  onEndDateChange(event: any): void {
    this.endDate = formatISO(new Date(event.value), { representation: 'complete' });
    this.onPickerDateChanged();
  }

  onTimeChange(event: any): void {
    this.startDate = formatISO(new Date(event.value), { representation: 'complete' });
    this.onPickerDateChanged();
  }

  onEndInputChange(event: any): void {
    this.endDate = formatISO(new Date(event.target.value), { representation: 'complete' });
    this.onPickerDateChanged();
  }

  private initializeDates(): void {
    this.minDate = this.min ? new Date(this.min) : (this.projectCreationDate || subYears(new Date(), 10));
    this.maxDate = this.max ? new Date(this.max) : endOfDay(new Date());
  }

  private onPickerDateChanged(): void {
    this.change.emit({
      startDate: this.startDate ? new Date(this.startDate).toISOString() : "",
      endDate: this.endDate ? new Date(this.endDate).toISOString() : ""
    })

  }

  private _setupPickerListeners(picker: any): void {
    if (!picker) return;

    const openedStream: Subject<any> = picker.openedStream;
    const closedStream: Subject<any> = picker.closedStream;

    openedStream.asObservable().pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.activity() || !this.datesColors.length) return;

      this.stateTimeout = setTimeout(() => {
        this.checkDates();
        this.uiTool.setupCalendarNavigationListeners(
          this.renderer,
          () => this.checkDates(),
          this.unlistenFns
        );
      }, 400);
    });

    closedStream.asObservable().pipe(takeUntil(this.destroy$)).subscribe(() => {
      clearTimeout(this.stateTimeout);
      this.unlistenFns.forEach(unlisten => unlisten());
      this.unlistenFns = [];
    });
  }

  checkDates(): void {
    if (!this.activity() || !this.datesColors.length) return;
    this.uiTool.applyCalendarColors(this.datesColors);
  }
}